import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';

import { ApiError } from '@starling/core/api/errors';
import { FormError, GhostButton } from '@/components/form/primitives';
import { useMyOrganizationsQuery, type MyOrganization } from '@/organization/hooks';
import { ListingForm, contactsFromOrganization } from './ListingForm';
import { useArchiveListingMutation, useCreateListingMutation, type Listing } from './hooks';
import styles from './PostPage.module.css';

// Публикация объявления — PostFlow прототипа, шаг 0 (раздел / заголовок /
// описание / город) + финал ✓. Шаги 1–2 прототипа (2ГИС + код) в V1.3-c НЕ
// здесь: это верификация организации, она живёт на «Моей организации»
// (V1.3-b) — /post лишь гейтит по её результату:
//   * организаций нет → подводка «нужна организация» → /my/organization;
//   * есть, но ни одной verified → подводка «подтвердите» → туда же;
//   * есть verified → форма публикации (при 2+ — выбор, от чьего имени).
//
// Сами поля — общий ListingForm (V1.3-e2: одна форма на публикацию и правку);
// здесь остаются гейты, мутация создания и локальный дефолт телефона.

/** Организация может публиковать: активна и верифицирована (гейт V1.1-b).
 *  Тот же гейт — у заявки на рекламу (V1.4, `ads/NewAdPage`). */
export function canPublish(org: MyOrganization): boolean {
  return org.status === 'active' && org.verified_via !== null;
}

/** …и с ПОДТВЕРЖДЁННЫМ контактом (№16-C): телефон по SMS или Telegram через бота. */
function hasContacts(org: MyOrganization): boolean {
  return org.phone_verified_at !== null || org.telegram_verified_at !== null;
}

/** Последний контактный телефон — удобный дефолт для следующей публикации. */
const PHONE_STORAGE_KEY = 'sv.post.contactPhone';

function readStoredPhone(): string {
  try {
    return localStorage.getItem(PHONE_STORAGE_KEY) ?? '';
  } catch {
    return '';
  }
}

function storePhone(phone: string): void {
  try {
    localStorage.setItem(PHONE_STORAGE_KEY, phone);
  } catch {
    /* приватный режим — просто не запоминаем */
  }
}

/** Подводка гейта: «нужна организация» / «подтвердите организацию» / «укажите контакты». */
export type GateKind = 'noOrg' | 'notVerified' | 'noContacts' | 'contactsUnverified';

/** Экспортируется: заявка на рекламу (V1.4) гейтит теми же подводками noOrg/notVerified. */
export function GateCard({ kind }: { kind: GateKind }): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className={styles.card}>
      <h2 className={styles.cardTitle}>{t(`post.gate.${kind}Title`)}</h2>
      <p className={styles.gateText}>{t(`post.gate.${kind}Text`)}</p>
      <Link to="/my/organization" className={styles.primaryLink}>
        {t('post.gate.action')}
      </Link>
    </div>
  );
}

/** Финал ✓ прототипа: ссылки на карточку и «Мои объявления», снятие. */
function DoneCard({ listing }: { listing: Listing }): JSX.Element {
  const { t } = useTranslation();
  const archiveMutation = useArchiveListingMutation();
  const [error, setError] = useState<string | null>(null);
  const archived = archiveMutation.isSuccess;

  return (
    <div className={styles.card}>
      <div className={styles.success}>
        <div className={styles.successIcon}>
          <Check size={24} aria-hidden="true" />
        </div>
        <p className={styles.successText}>
          {archived ? t('post.done.archived') : t('post.done.title')}
        </p>
        <FormError>{error}</FormError>
        <div className={styles.doneLinks}>
          {!archived && (
            <Link to={`/listings/${listing.id}`} className={styles.primaryLink}>
              {t('post.done.view')}
            </Link>
          )}
          <Link to="/my/listings" className={styles.secondaryLink}>
            {t('post.done.myListings')}
          </Link>
        </div>
        {!archived && (
          <div className={styles.doneArchive}>
            <GhostButton
              disabled={archiveMutation.isPending}
              onClick={() => {
                setError(null);
                archiveMutation.mutate(listing.id, {
                  onError: () => setError(t('post.done.archiveError')),
                });
              }}
            >
              {t('post.done.archive')}
            </GhostButton>
          </div>
        )}
      </div>
    </div>
  );
}

function PostForm({
  organizations,
  onPublished,
}: {
  organizations: MyOrganization[];
  onPublished: (listing: Listing) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const mutation = useCreateListingMutation();
  const [error, setError] = useState<string | null>(null);

  return (
    <div className={styles.card}>
      <ListingForm
        organizations={organizations}
        // Дефолты — город и контакты первой verified-организации (№16);
        // телефон из localStorage — лишь запасной вариант, если у организации
        // его нет (например, указан только Telegram).
        initial={{
          ...contactsFromOrganization(organizations[0]),
          phone: organizations[0]?.phone ?? readStoredPhone(),
        }}
        submitLabel={t('post.form.submit')}
        pending={mutation.isPending}
        error={error}
        onSubmit={(values) => {
          setError(null);
          mutation.mutate(
            {
              organization_id: values.organizationId,
              feed: values.feed,
              category_id: values.categoryId,
              title: values.title,
              description: values.description,
              city: values.city,
              district: values.district || null,
              contact_phone: values.phone || null,
              contact_whatsapp: values.whatsapp,
              contact_telegram: values.telegram || null,
            },
            {
              onSuccess: (listing) => {
                if (values.phone) storePhone(values.phone);
                onPublished(listing);
              },
              onError: (err) => {
                if (err instanceof ApiError) {
                  if (err.code === 'invalid_phone') return setError(t('post.errors.invalidPhone'));
                  if (err.code === 'invalid_telegram')
                    return setError(t('post.errors.invalidTelegram'));
                  if (err.code === 'contact_required')
                    return setError(t('post.errors.contactRequired'));
                  if (err.code === 'whatsapp_requires_phone')
                    return setError(t('post.errors.whatsappRequiresPhone'));
                  if (err.code === 'organization_no_contacts')
                    return setError(t('post.errors.noContacts'));
                  if (err.code === 'category_not_found') return setError(t('post.errors.category'));
                  // Гейт организации (403/404) — состояние успело измениться после
                  // загрузки страницы; ведём проверять статус.
                  if (err.status === 403 || err.status === 404)
                    return setError(t('post.errors.orgGate'));
                }
                setError(t('post.errors.generic'));
              },
            },
          );
        }}
      />
    </div>
  );
}

export function PostPage(): JSX.Element {
  const { t } = useTranslation();
  const query = useMyOrganizationsQuery();
  const navigate = useNavigate();
  const [published, setPublished] = useState<Listing | null>(null);

  let body: JSX.Element;
  if (published) {
    body = <DoneCard listing={published} />;
  } else if (query.isPending) {
    body = <div className={styles.state}>{t('post.loading')}</div>;
  } else if (query.isError) {
    body = (
      <div className={`${styles.state} ${styles.stateError}`}>
        {t('post.error')}
        <div>
          <button type="button" className={styles.retry} onClick={() => void query.refetch()}>
            {t('post.retry')}
          </button>
        </div>
      </div>
    );
  } else {
    const items = query.data.items;
    const verified = items.filter(canPublish);
    const publishable = verified.filter(hasContacts);
    if (items.length === 0) {
      body = <GateCard kind="noOrg" />;
    } else if (verified.length === 0) {
      body = <GateCard kind="notVerified" />;
    } else if (publishable.length === 0) {
      // Контактов нет ни у одной verified-организации → «укажите»; есть, но не
      // подтверждены → «подтвердите» (замечание приёмки 2026-09-11).
      const anyContact = verified.some((org) => org.phone !== null || org.telegram !== null);
      body = <GateCard kind={anyContact ? 'contactsUnverified' : 'noContacts'} />;
    } else {
      body = <PostForm organizations={publishable} onPublished={setPublished} />;
    }
  }

  return (
    <div className={styles.wrap}>
      <div className={styles.titleRow}>
        <h1 className={styles.pageTitle}>{t('post.title')}</h1>
        <button
          type="button"
          className={styles.close}
          aria-label={t('post.close')}
          onClick={() => navigate(-1)}
        >
          ×
        </button>
      </div>
      {body}
    </div>
  );
}
